import cx from 'classnames'
import { ErrorMessage, Formik } from 'formik'
import { ComponentType } from 'react'
import { Button, ButtonGroup, Col, CustomInput, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap'
import { up } from 'styled-breakpoints'
import styled from 'styled-components'
import * as Yup from 'yup'

const FormInput = styled(Input)`
  height: auto;
  font-size: 15px;
  line-height: 18px;
  letter-spacing: 0.5px;
  padding: 15px 16px;
`

const ToggleButton = styled(Button)`
  height: auto !important;
  font-size: 15px !important;
  line-height: 18px !important;
  letter-spacing: 0.5px !important;
  padding: 15px 16px !important;
`

const FORM_SCHEMA = Yup.object().shape({
  name: Yup.string().required('Campo requerido'),
  phone: Yup.string().required('Campo requerido'),
  email: Yup.string().email('Ingrese un email válido').required('Campo requerido'),
  operation: Yup.string().required('Campo requerido'),
  type: Yup.string().required('Campo requerido'),
  location: Yup.string().required('Campo requerido'),
})

export const TYPES = [
  'Bodega',
  'Casa',
  'Consultorio',
  'Departamento',
  'Espacio de Oficina',
  'Hotel',
  'Playa',
  'Quinta',
  'Rancho',
  'Terreno',
  'Townhouse',
  'Otro',
]

const Title = styled.h2`
  margin-bottom: 24px;
  color: #ffffff;
  font-size: 26px;
  font-weight: bold;
  line-height: 33px;
  text-align: center;
`

const Subtitle = styled.p`
  margin-bottom: 48px;
  color: #6bc0f9;
  font-size: 16px;
  line-height: 25px;
  text-align: center;
`

const Field = styled(FormGroup)`
  margin-bottom: 20px;

  ${up('sm')} {
    margin-bottom: 40px;
  }
`

const FieldLabel = styled(Label)`
  padding-left: 16px;
  margin-bottom: 12px;
  color: #6bc0f9;
  font-weight: 500;
`

interface Props {
  loading: boolean
  onSubmit: (values: any) => void
}

export const Form: ComponentType<Props> = ({ loading, onSubmit }) => (
  <Formik
    initialValues={{ name: '', phone: '', email: '', operation: '', type: '', location: '', message: '' }}
    validationSchema={FORM_SCHEMA}
    onSubmit={(values) => onSubmit(values)}
  >
    {({ values, touched, errors, handleBlur, handleChange, handleSubmit }) => (
      <form onSubmit={handleSubmit}>
        <Title>Tu propiedad en Yucatán Premier</Title>
        <Subtitle>Completa el formulario para recibir atención personalizada.</Subtitle>
        <Field>
          <FieldLabel htmlFor="name">Nombre completo</FieldLabel>
          <FormInput
            id="name"
            name="name"
            bsSize="lg"
            value={values.name}
            invalid={Boolean(errors.name && touched.name)}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <ErrorMessage name="name" component={FormFeedback} />
        </Field>
        <Row>
          <Col lg={6}>
            <Field>
              <FieldLabel htmlFor="phone">Teléfono de contacto</FieldLabel>
              <FormInput
                id="phone"
                name="phone"
                bsSize="lg"
                value={values.phone}
                invalid={Boolean(errors.phone && touched.phone)}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              <ErrorMessage name="phone" component={FormFeedback} />
            </Field>
          </Col>
          <Col lg={6}>
            <Field>
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <FormInput
                id="email"
                name="email"
                bsSize="lg"
                value={values.email}
                invalid={Boolean(errors.email && touched.email)}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              <ErrorMessage name="email" component={FormFeedback} />
            </Field>
          </Col>
        </Row>
        <Row>
          <Col lg={6}>
            <Field>
              <FieldLabel>Operación</FieldLabel>
              <ButtonGroup
                size="lg"
                className={cx('d-flex rounded', {
                  'border border-danger': Boolean(errors.operation && touched.operation),
                })}
              >
                <ToggleButton
                  tag="label"
                  className="flex-fill mb-0"
                  color={values.operation === 'renta' ? 'primary' : 'light'}
                >
                  Renta
                  <Input
                    type="radio"
                    name="operation"
                    value="renta"
                    className="d-none"
                    checked={values.operation === 'renta'}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </ToggleButton>
                <ToggleButton
                  tag="label"
                  className="flex-fill mb-0"
                  color={values.operation === 'venta' ? 'primary' : 'light'}
                >
                  Venta
                  <Input
                    type="radio"
                    name="operation"
                    value="venta"
                    className="d-none"
                    checked={values.operation === 'venta'}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </ToggleButton>
              </ButtonGroup>
              <ErrorMessage name="operation" component={FormFeedback} className="d-block" />
            </Field>
          </Col>
          <Col lg={6}>
            <Field>
              <FieldLabel htmlFor="type">Tipo de propiedad</FieldLabel>
              <FormInput
                as={CustomInput}
                type="select"
                id="type"
                name="type"
                bsSize="lg"
                value={values.type}
                invalid={Boolean(errors.type && touched.type)}
                onBlur={handleBlur}
                onChange={handleChange}
              >
                <option value="">Elige una opción</option>
                {TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </FormInput>
              <ErrorMessage name="type" component={FormFeedback} />
            </Field>
          </Col>
        </Row>
        <Row className="justify-content-end">
          <Col lg={6}>
            <Field>
              <FieldLabel htmlFor="location">Ubicación de la propiedad</FieldLabel>
              <FormInput
                id="location"
                name="location"
                bsSize="lg"
                value={values.location}
                invalid={Boolean(errors.location && touched.location)}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              <ErrorMessage name="location" component={FormFeedback} />
            </Field>
          </Col>
        </Row>
        <Field>
          <FieldLabel htmlFor="message">
            Mensaje <span className="font-weight-100">(Opcional)</span>
          </FieldLabel>
          <FormInput
            type="textarea"
            rows={4}
            id="message"
            name="message"
            bsSize="lg"
            value={values.message}
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </Field>
        <Button type="submit" color="primary" size="lg" disabled={loading} block>
          {loading ? 'Enviando solicitud...' : 'Enviar solicitud'}
        </Button>
      </form>
    )}
  </Formik>
)
